/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  RACTextbox,
  RACSelect,
  RACButton,
  CircularProgress,
} from '@rentacenter/racstrap';
import { EscalationOption, ReferenceKeys } from '../../../types/types';
import { useUserStateContext } from '../../../context/user/user-contexts';
import { customerStyles } from '../../customerStyles';
import {
  buildUploadPayload,
  computeRequiredFields,
  fetchEscalatedData,
  formatPhone,
  getCategoryIdByValue,
  getFilteredActions,
  getFilteredSubcategories,
  getSubCategoryIdByValue,
  gridStyle,
  isFieldMissing,
  isValidReferenceKey,
  prepareApiPayload,
  readFileAsBase64,
  removeEmptyFields,
  toOptions,
  uniqueifyFilenames,
  validateRequiredFields,
} from '../../shared/utils';
import { ReactComponent as Attachment } from '../../../assets/images/attachment-icon.svg';
import {
  requiredFields as baseRequiredFields,
  CSM_REQUEST_ERROR_MESSAGE,
  CSM_REQUEST_ERROR_MESSAGE_2,
  CSM_SUCCESS_MESSAGE,
  CSM_SUCCESS_MESSAGE_2,
  fields,
  initialBasicInfo,
} from '../../../constants/constants';
import { ApiSuccessModal } from '../../../common/ApiSuccess/ApiSuccess';
import { useHistory } from 'react-router-dom';
import {
  attachmentsUrl,
  CreateTicket,
  getAgreementData,
} from '../../../api/user';
import { SearchableDropdown } from '../../shared/SearchableDropdown';
import Footer from '../../shared/Footer';
import CustomTextArea from '../../shared/CustomTextArea';
import { CustomDropdown } from '../../shared/CustomDropdown';
import { useReferenceContext } from '../../../context/CustomerService/ReferenceProvider';

const BasicInformation: React.FC = () => {
  const classes = customerStyles();
  const history = useHistory();
  const { references, loading } = useReferenceContext();
  const { user, isPending } = useUserStateContext();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [screenLoader, setScreenLoader] = useState<boolean>(false);
  const [isApiSuccessOpen, setIsApiSuccessOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const [apiSubMessage, setApiSubMessage] = useState('');
  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [basicInfo, setBasicInfo] = useState(initialBasicInfo);
  const [escalationOptions, setEscalationOptions] = useState<
    EscalationOption[]
  >([{ label: 'Select', value: '' }]);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [isAssignmentSelected, setIsAssignmentSelected] = useState(false);
  const [isEscalationSelected, setIsEscalationSelected] = useState(false);
  const [agreementSearch, setAgreementSearch] = useState(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const selectedCategoryId = useMemo(
    () => getCategoryIdByValue(references?.category as any, basicInfo.category),
    [references?.category, basicInfo.category]
  );

  const selectedSubCategoryId = useMemo(
    () =>
      getSubCategoryIdByValue(
        references?.sub_category as any,
        basicInfo.sub_category
      ),
    [references?.sub_category, basicInfo.sub_category]
  );
  const storeOptions = useMemo(
    () =>
      user?.stores?.map((store: any) => ({
        label: `${store.accountingUnitNumber} - ${store.storeType} - ${
          store.addressLine1
        } - ${store.city} - ${store.state} -- ${formatPhone(
          store.workPhoneNumber
        )}`,
        value: store.accountingUnitNumber,
      })) ?? [],
    [user?.stores]
  );

  const updateBasicInfo = (newData: any) => {
    setBasicInfo((prevState) => ({
      ...prevState,
      ...newData,
    }));
  };

  const filteredSubCategoryOptions = useMemo(() => {
    const raw = getFilteredSubcategories(
      selectedCategoryId,
      references?.sub_category as any
    );
    return toOptions(raw);
  }, [references?.sub_category, selectedCategoryId]);

  const filteredActionOptions = useMemo(() => {
    const raw = getFilteredActions(
      selectedSubCategoryId,
      references?.action_type as any
    );
    return toOptions(raw);
  }, [references?.action_type, selectedSubCategoryId]);

  const dynamicRequiredFields = useMemo(
    () =>
      computeRequiredFields({
        baseRequiredFields,
        basicInfo: basicInfo as any,
        references: references as any,
        selectedCategoryId,
        selectedSubCategoryId,
        filteredSubCategoryOptions,
        filteredActionOptions,
      }),
    [
      basicInfo,
      references,
      selectedCategoryId,
      selectedSubCategoryId,
      filteredSubCategoryOptions,
      filteredActionOptions,
    ]
  );

  const handleButtonClick = useCallback(
    () => fileInputRef.current?.click(),
    []
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const existingAttachments = basicInfo.attachments ?? [];
      const uniqueFiles = uniqueifyFilenames(
        Array.from(files),
        existingAttachments.map((a) => a.attachmentName)
      );

      try {
        setScreenLoader(true);
        const payload = await buildUploadPayload(uniqueFiles, readFileAsBase64);
        const result = await attachmentsUrl(payload);
        if (result && Array.isArray(result.attachments)) {
          const formattedAttachments = result.attachments.map(
            (item: { documentName: string; documentLocation: string }) => ({
              attachmentName: item.documentName,
              attachmentLocation: item.documentLocation,
            })
          );
          updateBasicInfo({
            attachments: [...existingAttachments, ...formattedAttachments],
          });
        } else {
          console.warn('Unexpected response format:', result);
        }
      } catch (error) {
        console.error('Failed to upload and store attachments:', error);
      } finally {
        setScreenLoader(false);
        if (event.target) event.target.value = '';
      }
    },
    [basicInfo.attachments]
  );

  const sanitizeInput = (name: string, value: string) =>
    ['firstName', 'lastName'].includes(name)
      ? value?.replace(/[^A-Z-',.; a-z]/gi, '')
      : value;

  const getMaxLength = (name: string) =>
    ((
      {
        agreementnumber: 19,
        firstName: 30,
        lastName: 30,
        callback: 14,
        alterphonenumb: 14,
      } as Record<string, number>
    )[name] ?? 30);

  const getType = (name: string) =>
    ['callback', 'alterphonenumb'].includes(name) ? 'phoneno' : 'text';
  const getPattern = (name: string) =>
    ['callback', 'alterphonenumb'].includes(name) ? '\\D*' : undefined;

  const handleSubmit = useCallback(async () => {
    try {
      setScreenLoader(true);
      const apiPayload = removeEmptyFields(prepareApiPayload(basicInfo, user));
      const res = await CreateTicket(apiPayload);
      if (res?.ticketId && res?.ticketNumber) {
        updateBasicInfo({ ticketNumber: res?.ticketNumber });
        setApiMessage(CSM_SUCCESS_MESSAGE);
        setApiSubMessage(`${CSM_SUCCESS_MESSAGE_2} ${res.ticketNumber}`);
        setIsApiSuccessOpen(true);
        setIsApiError(false);
      } else {
        setIsApiError(true);
        setApiMessage(CSM_REQUEST_ERROR_MESSAGE);
        setApiSubMessage(CSM_REQUEST_ERROR_MESSAGE_2);
        setIsApiSuccessOpen(true);
      }
    } catch (error) {
      setIsApiError(true);
      setApiMessage(CSM_REQUEST_ERROR_MESSAGE);
      setApiSubMessage(CSM_REQUEST_ERROR_MESSAGE_2);
      setIsApiSuccessOpen(true);
      console.error('Error submitting ticket:', error);
    } finally {
      setScreenLoader(false);
    }
  }, [basicInfo, user]);

  const handleBlur = async () => {
    let lastRequestId = 0;

    const agreementNumber = (basicInfo.agreementnumber ?? '').trim();
    if (!agreementNumber) return;

    const requestId = ++lastRequestId;

    const payload = { agreementNumber, limit: 1, offset: 0 };

    try {
      setAgreementSearch(true);
      const res = await getAgreementData(payload);
      if (requestId !== lastRequestId) return;

      const storeNumber = res?.agreementSearch?.[0]?.storeNumber;
      if (storeNumber) {
        updateBasicInfo({ storenumber: storeNumber });
        setEscalationLoading(true);
        const options = await fetchEscalatedData(storeNumber);
        setEscalationOptions(options);
        setEscalationLoading(false);
        setAgreementSearch(false);
      } else {
        console.warn('No storeNumber for agreement:', agreementNumber);
        updateBasicInfo({ storenumber: '' });
        setAgreementSearch(false);
      }
    } catch (err) {
      setAgreementSearch(false);
      setEscalationLoading(false);
      if (requestId !== lastRequestId) return;
      console.error('Agreement lookup failed:', err);
    }
  };

  const scrollToFirstMissing = () => {
    const firstMissing = fields.find(({ name }) =>
      isFieldMissing(
        name as string,
        (basicInfo as any)[name],
        dynamicRequiredFields
      )
    );
    if (firstMissing) {
      const el = document.querySelector(`[name="${firstMissing.name}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as HTMLElement | null)?.focus?.();
    }
  };

  const handleTrySubmit = async () => {
    setShowErrors(true);
    const canSubmit = validateRequiredFields(
      dynamicRequiredFields as any,
      basicInfo as any
    );

    if (!canSubmit) {
      scrollToFirstMissing();
      return;
    }
    setShowErrors(false);
    await handleSubmit();
  };

  return (
    <>
      {isApiSuccessOpen && (
        <ApiSuccessModal
          open={isApiSuccessOpen}
          message={apiMessage}
          secondmsg={apiSubMessage}
          error={isApiError}
          onClose={() => {
            setApiMessage('');
            setApiSubMessage('');
            setIsApiSuccessOpen(false);
            setIsApiError(false);
            if (!isApiError) {
              history.push(`/csm/info/${basicInfo.ticketNumber}`);
            }
          }}
        />
      )}

      {screenLoader ? (
        <Grid className={classes.masterLoader}>
          <Grid className={classes.Loader}>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : null}

      <Grid
        container
        id="bsicinfo"
        className={`${classes.mb16} ${classes.px2}`}
      >
        <Grid item md={12}>
          <Typography className={classes.title}>
            Create a Customer Incident
          </Typography>
        </Grid>

        <Grid item md={12} className={classes.mb120}>
          <Card className={`${classes.card} ${classes.w100}`}>
            <CardContent>
              <Grid container spacing={3} justifyContent="space-between">
                {fields.map(
                  ({ name, label, disabled: isFieldDisabled = false }) => {
                    const value = (basicInfo as any)[name] ?? '';
                    const isRequired = dynamicRequiredFields.includes(
                      name as any
                    );

                    if (name === 'storenumber') {
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <SearchableDropdown
                            options={storeOptions}
                            selection={async (val) => {
                              updateBasicInfo({ storenumber: val });
                              if (!val) {
                                setEscalationOptions([
                                  { label: 'Select', value: '' },
                                ]);
                                setIsEscalationSelected(false);
                              } else {
                                setEscalationLoading(true);
                                const options = await fetchEscalatedData(val);
                                setEscalationOptions(options);
                                setEscalationLoading(false);
                              }
                            }}
                            defaultValue={value as string}
                            loading={isPending || agreementSearch}
                            inputLabel={label}
                            required={isRequired}
                            isDisabled={isPending}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    if (name === 'category') {
                      const categoryOptions = toOptions(
                        (references as any)[name as ReferenceKeys]
                      );
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <CustomDropdown
                            inputLabel={label}
                            loading={loading}
                            required={isRequired}
                            options={categoryOptions}
                            defaultValue={value as string}
                            onChange={(e: string) => {
                              updateBasicInfo({
                                category: e,
                                sub_category: '',
                                action_type: '',
                              });
                            }}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    if (name === 'sub_category') {
                      const hasSubs = filteredSubCategoryOptions.length > 0;
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <CustomDropdown
                            inputLabel={label}
                            loading={loading}
                            required={isRequired}
                            isDisabled={!selectedCategoryId || !hasSubs}
                            options={filteredSubCategoryOptions}
                            defaultValue={value as string}
                            onChange={(e: string) => {
                              updateBasicInfo({
                                sub_category: e,
                                action_type: '',
                              });
                            }}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    if (name === 'action_type') {
                      const hasActions = filteredActionOptions.length > 0;
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <CustomDropdown
                            inputLabel={label}
                            loading={loading}
                            required={isRequired}
                            isDisabled={!selectedCategoryId || !hasActions}
                            options={filteredActionOptions}
                            defaultValue={value as string}
                            onChange={(e: any) => {
                              updateBasicInfo({ [name]: e });
                            }}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }
                    if (name === 'shortDescription') {
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <Typography className={classes.notesLabel}>
                            {label}{' '}
                            {isRequired && (
                              <span className={`${classes.reqField}`}>*</span>
                            )}
                          </Typography>
                          <CustomTextArea
                            value={value as string}
                            onChange={(e: any) => {
                              updateBasicInfo({ [name]: e });
                            }}
                            minHeight={50}
                            maxHeight={100}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    if (name === 'description') {
                      return (
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          key={name}
                          style={gridStyle}
                        >
                          <Typography className={classes.notesLabel}>
                            {label}{' '}
                            {isRequired && (
                              <span className={`${classes.reqField}`}>*</span>
                            )}
                          </Typography>
                          <CustomTextArea
                            value={value as string}
                            onChange={(e: any) => {
                              updateBasicInfo({ [name]: e });
                            }}
                            minHeight={100}
                            maxHeight={300}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    if (name == 'agreementnumber') {
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <RACTextbox
                            type={getType(name)}
                            name={name}
                            inputlabel={label}
                            maxlength={getMaxLength(name)}
                            pattern={getPattern(name)}
                            isCurrency={false}
                            disabled={isFieldDisabled}
                            required={isRequired}
                            value={value as string}
                            Onblur={handleBlur}
                            OnChange={(e: any) => {
                              const value = sanitizeInput(name, e.target.value);
                              updateBasicInfo({ [name]: value });
                            }}
                          />
                        </Grid>
                      );
                    }

                    if (isValidReferenceKey(name as string)) {
                      const opts = toOptions(
                        (references as any)[name as ReferenceKeys]
                      );
                      return (
                        <Grid
                          item
                          lg={6}
                          md={6}
                          sm={6}
                          key={name}
                          style={gridStyle}
                        >
                          <CustomDropdown
                            inputLabel={label}
                            loading={loading}
                            required={isRequired}
                            options={opts}
                            defaultValue={value as string}
                            onChange={(e: any) => {
                              updateBasicInfo({ [name]: e });
                            }}
                          />
                          {showErrors &&
                            isFieldMissing(
                              name,
                              value,
                              dynamicRequiredFields
                            ) && (
                              <Typography className={classes.errorText}>
                                Required
                              </Typography>
                            )}
                        </Grid>
                      );
                    }

                    return (
                      <Grid
                        item
                        lg={6}
                        md={6}
                        sm={6}
                        key={name}
                        style={gridStyle}
                      >
                        <RACTextbox
                          type={getType(name)}
                          name={name}
                          inputlabel={label}
                          maxlength={getMaxLength(name)}
                          pattern={getPattern(name)}
                          isCurrency={false}
                          disabled={isFieldDisabled}
                          required={isRequired}
                          value={value as string}
                          OnChange={(e: any) => {
                            const value = sanitizeInput(name, e.target.value);
                            updateBasicInfo({ [name]: value });
                          }}
                          errorMessage={
                            showErrors &&
                            isFieldMissing(name, value, dynamicRequiredFields)
                              ? 'Required'
                              : undefined
                          }
                        />
                      </Grid>
                    );
                  }
                )}

                <Grid
                  container
                  id="assignmentinfo"
                  className={`${classes.mb16} ${classes.px2}`}
                >
                  <Grid item md={12}>
                    <Typography className={`${classes.title} ${classes.mb15}`}>
                      Assignment Details
                    </Typography>
                  </Grid>
                  <Grid container spacing={3} justifyContent="space-between">
                    <Grid item lg={6} md={6} sm={6} style={gridStyle}>
                      <CustomDropdown
                        inputLabel="Assignment Group"
                        loading={loading}
                        options={toOptions(references?.assignment_group)}
                        isDisabled={isEscalationSelected}
                        defaultValue={
                          basicInfo.assignment?.assignmentGroup ?? ''
                        }
                        onChange={(e: any) => {
                          updateBasicInfo({
                            assignment: {
                              ...basicInfo.assignment,
                              assignmentGroup: e,
                            },
                          });
                          setIsAssignmentSelected(!!e);
                          setIsEscalationSelected(false);
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    spacing={3}
                    justifyContent="space-between"
                    className={classes.escaltedinfo}
                  >
                    <Grid item lg={6} md={6} sm={6} style={gridStyle}>
                      <RACSelect
                        name="escalatedTo"
                        inputLabel="Escalated to"
                        loading={escalationLoading}
                        options={escalationOptions}
                        defaultValue={basicInfo.assignment?.escalatedTo ?? ''}
                        isDisabled={isAssignmentSelected}
                        onChange={(e: any) => {
                          const selectedValue = e.target.value;
                          const selectedOption = escalationOptions.find(
                            (opt) => opt.value === selectedValue
                          );

                          updateBasicInfo({
                            assignment: {
                              ...basicInfo.assignment,
                              escalatedTo: selectedValue,
                              esName: selectedOption?.esName ?? '',
                              escalationContact:
                                selectedOption?.phone_number ?? 'none',
                              role: selectedOption?.role ?? '',
                            },
                          });
                          setIsEscalationSelected(!!selectedValue);
                          setIsAssignmentSelected(false);
                        }}
                      />
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} style={gridStyle}>
                      <RACTextbox
                        name={'escalationContact'}
                        inputlabel="Escalation Contact"
                        isCurrency={false}
                        disabled={true}
                        value={basicInfo.assignment?.escalationContact ?? ''}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={2}
            style={{
              marginTop: '20px',
              paddingLeft: '50px',
              flexWrap: 'nowrap',
            }}
          >
            <Grid item>
              <RACButton
                variant="outlined"
                color="secondary"
                id="uploadDocument"
                className={classes.uploadButton}
                onClick={handleButtonClick}
                startIcon={<Attachment />}
              >
                Add Attachments
                <input
                  ref={fileInputRef}
                  multiple
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,image/*,.txt,.xlsx,.xls,.doc,.docx,.mp4,.avi"
                  style={{ display: 'none' }}
                />
              </RACButton>
            </Grid>
            {(basicInfo?.attachments?.length ?? 0) > 0 && (
              <Grid
                item
                style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}
              >
                {basicInfo.attachments?.map((file, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    style={{ cursor: 'pointer' }}
                  >
                    {file.attachmentName}
                  </Typography>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Footer handleSubmit={handleTrySubmit} name={'Submit'} />
    </>
  );
};

export default BasicInformation;
